/**
 * Conditional call forwarding is a carrier feature, not an Omnetic feature: the dealer dials a GSM
 * code on their own handset and the carrier starts forwarding. Nothing here is stored and nothing
 * is sent anywhere — the codes are a pure function of the Omnetic number the calls should reach and
 * the ring time the dealer picked, which is why this module is the single place a code string is
 * ever formatted.
 *
 * Two codes are deliberately never produced, and the sweep in the accompanying spec enforces it:
 *
 * - `**004*<number>**<seconds>#` sets all three conditions in one dial, discarding whatever
 *   per-condition rules the handset already had — a forward to a personal voicemail or a partner's
 *   phone that has nothing to do with us. It looks like a shortcut and erases quietly.
 * - `##002#` / `##004#` erase every forwarding rule at once, with the same collateral damage.
 *
 * Per-condition codes also degrade gracefully: a dealer who only wants busy and unreachable
 * forwarding simply skips the no-answer code.
 */

/**
 * The GSM specification allows the no-answer timer in five-second steps up to 30 seconds. This is a
 * property of the protocol, not of a carrier or a market, so it is a constant rather than
 * configuration — handsets reject or silently ignore anything else.
 */
export const GSM_RING_SECONDS_STEPS = [5, 10, 15, 20, 25, 30] as const;

export type GsmRingSeconds = (typeof GSM_RING_SECONDS_STEPS)[number];

export const DEFAULT_GSM_RING_SECONDS: GsmRingSeconds = 20;

export interface GsmForwardingConditionCodes {
  noAnswer: string;
  busy: string;
  unreachable: string;
}

export interface GsmForwardingCodes {
  /** Dialled once on the forwarding handset to start sending that kind of missed call to us. */
  activation: GsmForwardingConditionCodes;
  /** Turns one condition back off and leaves the other two alone. */
  deactivation: GsmForwardingConditionCodes;
  /**
   * Asks the network what it currently does with each kind of call. The reply comes from the
   * carrier, which is the only party that knows the truth, so this is how the dealer verifies the
   * setup worked.
   */
  interrogation: GsmForwardingConditionCodes & {all: string};
}

/**
 * A dial string cannot carry spaces. Provider numbers arrive in E.164 already, but a number typed
 * or pasted anywhere else may be spaced for legibility.
 */
const toDiallableNumber = (phoneNumber: string) => phoneNumber.replace(/\s/g, '');

export const getGsmForwardingCodes = (
  phoneNumber: string,
  ringSeconds: GsmRingSeconds
): GsmForwardingCodes => {
  const destination = toDiallableNumber(phoneNumber);

  return {
    activation: {
      noAnswer: `**61*${destination}**${ringSeconds}#`,
      busy: `**67*${destination}#`,
      unreachable: `**62*${destination}#`,
    },
    deactivation: {
      noAnswer: '##61#',
      busy: '##67#',
      unreachable: '##62#',
    },
    interrogation: {
      noAnswer: '*#61#',
      busy: '*#67#',
      unreachable: '*#62#',
      all: '*#004#',
    },
  };
};
